import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader, ErrorFallback, TextInput } from 'smart-react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import FileUpload from '../../../Core/Components/InputFile/InputFile';
import { xIcon } from '@progress/kendo-svg-icons';
import {
  buildNotification,
  GenerateNotification,
  isValidate,
  isFromValid,
  DescriptionTextArea,
} from 'smart-react';
import TenantDropdown from '../../../Core/Components/TenantDropdown/TenantDropdown';
import { createBlob, updateBlob, downloadBlob } from '../Services/BlobService';
import { isAdminRole } from '../../../../Utils/Auth/ValidatePermissions';
import { base64ToObject } from '../../../../Utils/Common/commonUtils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { Error } from '@progress/kendo-react-labels';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Core/Context/AuthContext';
import NoPermission from '../../../Core/NoPermission/NoPermission';
import { downloadFile } from '../Utils/Utils';
import {
  ValidatePermissions,
  findTenant,
  findTenantById,
} from '../../../../Utils/Auth/ValidatePermissions';
import { getBlob } from '../Services/BlobService';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import { Chip } from '@progress/kendo-react-buttons';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add New Blob main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Blob component.
 */
const AddBlob = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const { user, tenantID } = useAuth(); // Get tenantID using Auth Context
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [isTextArea, setIsTextArea] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [noRecord, setNoRecord] = React.useState(false);
  const [tenant, setTenant] = React.useState(findTenantById(tenantID));
  const [isModifiedBlob, setIsModifiedBlob] = React.useState(true);
  const [formData, setFormData] = React.useState({
    Id: '',
    TenantId: tenantID,
    TenantName: tenant?.TenantName,
    InstName: '',
    EnvId: '',
    BlobId: '',
    BlobDesc: '',
    AppId: '',
    AppFeature: '',
    IsUnique: true,
    Extension: '',
    Encoding: '',
    Zipped: '',
    Blob: '',
    Active: true,
  });
  const fileUploadRef = React.useRef(null);

  const [validateForm, setValidateForm] = React.useState(false);
  const requiredFields = ['EnvId', 'BlobId', 'AppId', 'AppFeature', 'Blob'];
  const [editorField, setEditorField] = React.useState({
    name: 'description',
    value: formData?.description,
    label: 'Description',
  });
  const { state } = useLocation();
  const [tempState, setTempState] = React.useState(state);
  const [fileInputKey, setFileInputKey] = React.useState(0);

  /**
   * remove the File
   */
  const handleRemove = () => {
    setFileInputKey((prevKey) => prevKey + 1);
    setFormData((prevState) => ({
      ...prevState,
      Extension: '',
      Blob: '',
      InstName: '',
    }));
  };

  /**
   * download the blob data
   */
  const downloadItemClick = async () => {
    setLoading(true);
    let data = formData;
    let response = await downloadBlob({
      data,
      isServiceWorker: false,
    });
    try {
      if (response.IsSuccess) {
        setLoading(false);
        await downloadFile(response?.Payload);
      } else {
        GenerateNotification(
          buildNotification(ERROR_MESSAGE),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get Blob Detail from API for edit Blob
   * @param {*} stateData
   */
  const fetchBlobData = async (stateData) => {
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await getBlob({
        data: refObj,
        isServiceWorker: false,
      });
      const blobData = response.Payload;
      if (blobData) {
        setTempState({ ...stateData, dataSet: blobData, edit: true });
        setFormData(blobData);
        setTenant(findTenant(blobData));
        setNoRecord(false);
        setIsModifiedBlob(false);
        if (ValidatePermissions(blobData)) {
          setDetailsDataModified(true);
        }
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (tempState || (tenantId && tenantId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchBlobData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setTenant(findTenant(dataSet));
        if (ValidatePermissions(dataSet)) {
          setDetailsDataModified(true);
        } else {
          setDetailsDataModified(false);
        }
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  /**
   * on update the tenantID update the formData
   */
  React.useEffect(() => {
    if (!tempState?.edit && tenantId === 'new') {
      let tempTenant = findTenantById(tenantID);
      setTenant(tempTenant);
      setFormData((prevState) => ({
        ...prevState,
        TenantId: tenantID,
        TenantName: tempTenant?.TenantName ? tempTenant?.TenantName : '',
      }));
    }
  }, [tenantID]);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * handle the input file trigger and save the data form data
   * @param {Object} event
   * @returns {void}
   */

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Get file extension
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const name = event?.target?.name;

      const reader = new FileReader();
      reader.onload = (e) => {
        // The result is already in base64 format, just need to remove the data URL prefix
        const base64String = e.target.result.split(',')[1];
        setFormData((prevState) => ({
          ...prevState,
          Extension: fileExtension,
          InstName: file.name,
          [name]: base64String,
        }));
      };
      reader.onerror = () => {
        console.error('Failed to read the file.');
      };
      // Read as base64 data URL
      reader.readAsDataURL(file);
    } else {
      setFormData((prevState) => ({
        ...prevState,
        Extension: '',
        Blob: '',
        InstName: '',
      }));
    }
  };

  /**
   * submit the blob data to api
   * @param {boolean} isExit
   * @returns {void}
   */
  const onSave = async ({ isExit = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
    };
    let response;
    let method;

    if (tempState?.edit) {
      method = updateBlob;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createBlob;
      notificationMetaData = CREATE_MESSAGE;
      setFormData({
        Id: '',
        TenantId: tenantID,
        TenantName: tenant?.TenantName,
        InstName: '',
        EnvId: '',
        BlobId: '',
        BlobDesc: '',
        AppId: '',
        AppFeature: '',
        IsUnique: true,
        Extension: '',
        Encoding: '',
        Zipped: '',
        Blob: '',
        Active: true,
      });
      setFileInputKey((prevKey) => prevKey + 1);
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        if (isExit) {
          navigate('/admin/blobs');
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to blobs listing screen
   */
  const onExit = () => {
    navigate('/admin/blobs');
  };

  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto add-blob'>
        {!isAdminRole(user) && <NoPermission />}
        {isAdminRole(user) && (
          <React.Suspense fallback={<Loader />}>
            {loading && <Loader />}
            {noRecord ? (
              <NoRecordFound
                recordNotFoundText={'No Data found'}
                onExit={onExit}
              />
            ) : (
              <>
                <div className={`${isTextArea ? '' : 'k-d-none'}`}>
                  <DescriptionTextArea
                    onChange={handleInputChange}
                    label={'Description'}
                    renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                    validateForm={validateForm}
                    formData={formData}
                    setFormData={setFormData}
                    detailsDataModified={detailsDataModified}
                    isTextArea={isTextArea}
                    setIsTextArea={setIsTextArea}
                    editorField={editorField}
                    setEditorField={setEditorField}
                    row={2}
                  />
                </div>
                <div
                  className={`add-rf-profile-group main-page-section add-page-section ${!isTextArea ? '' : 'k-d-none'}`}
                >
                  <Card className='tophead'>
                    <CardHeader>
                      <div className='action-buttons-container'>
                        <Link
                          className='action-icon-wrapper !k-d-block'
                          to={`/admin/blobs`}
                        >
                          <FontAwesomeIcon
                            className='k-mr-2 k-pt-3'
                            icon={faArrowLeft}
                          />
                        </Link>
                        <div>
                          <p className='display-text k-mb-0'>
                            Blob: {formData?.BlobId}
                          </p>
                        </div>
                      </div>
                      {formData?.BlobDesc && (
                        <div className='desciption-label'>
                          <span className='k-d-block k-mb-2'>
                            {formData?.BlobDesc}
                          </span>
                        </div>
                      )}
                    </CardHeader>
                  </Card>
                  <Card className='k-mt-3 detail-card'>
                    <CardHeader>
                      <h6 className='card-title-secondary'>Details</h6>
                    </CardHeader>
                    <CardBody className='detail-card-body'>
                      <div>
                        <Form
                          onSubmit={onSave}
                          render={(formRenderProps) => (
                            <div className='card-content'>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.TenantName) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <TenantDropdown
                                    textField='TenantName'
                                    label='Tenant'
                                    dataItemKey='TenantId'
                                    className='tenant-dropdown k-align-self-end'
                                    tenant={tenant}
                                    setTenant={setTenant}
                                    defaultItem={{
                                      TenantName: 'Select Tenant ...',
                                      disabled: true,
                                      OrganizationPrefix: '',
                                    }}
                                    editMode={tempState?.edit ? 'ReadOnly' : ''}
                                    filterable={true}
                                    tenantFormId='tenant_id'
                                    tenantFormName='TenantName'
                                    setFormData={setFormData}
                                    requiredField={true}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.TenantName,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                              </div>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.BlobId) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'BlobId'}
                                    id={'BlobId'}
                                    name={'BlobId'}
                                    data={formData?.BlobId}
                                    onChange={handleInputChange}
                                    label={'Blob Id'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.BlobId,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.EnvId) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'EnvId'}
                                    id={'EnvId'}
                                    name={'EnvId'}
                                    data={formData?.EnvId}
                                    onChange={handleInputChange}
                                    label={'Environment Id'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.EnvId,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                              </div>

                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.AppId) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'AppId'}
                                    id={'AppId'}
                                    name={'AppId'}
                                    data={formData?.AppId}
                                    onChange={handleInputChange}
                                    label={'App Id'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.AppId,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.AppFeature) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'AppFeature'}
                                    id={'AppFeature'}
                                    name={'AppFeature'}
                                    data={formData?.AppFeature}
                                    onChange={handleInputChange}
                                    label={'App Feature'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.AppFeature,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                              </div>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper field-wrapper-description field-wrapper-100`}
                                >
                                  <DescriptionTextArea
                                    onChange={handleInputChange}
                                    label={'Description'}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    validateForm={validateForm}
                                    formData={formData}
                                    setFormData={setFormData}
                                    detailsDataModified={detailsDataModified}
                                    isTextArea={isTextArea}
                                    setIsTextArea={setIsTextArea}
                                    editorField={{
                                      name: 'BlobDesc',
                                      value: formData?.BlobDesc,
                                      label: 'Description',
                                    }}
                                    setEditorField={setEditorField}
                                    row={2}
                                  />
                                </div>
                              </div>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper field-wrapper-description field-wrapper-100 ${isValidate(validateForm, formData?.Blob) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <FileUpload
                                    label='Upload File'
                                    ref={fileUploadRef}
                                    id='Blob'
                                    key={fileInputKey}
                                    name={'Blob'}
                                    requiredField={true}
                                    onChange={handleFileChange} // Pass the handler to the child component
                                    required
                                  />
                                  {isValidate(validateForm, formData?.Blob) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                              </div>
                              {formData?.InstName && (
                                <div className='k-d-flex single-field-row'>
                                  <div
                                    className={`field-wrapper field-wrapper-chip`}
                                  >
                                    <Chip
                                      removable={true}
                                      onRemove={handleRemove}
                                      removeSvgIcon={xIcon}
                                    >
                                      <span className='k-chip-label'>
                                        {formData?.InstName}
                                      </span>
                                      {!isModifiedBlob && (
                                        <span
                                          className='k-i-download k-icon k-ml-1'
                                          onClick={downloadItemClick}
                                        />
                                      )}
                                    </Chip>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </CardBody>
                  </Card>
                  {detailsDataModified && (
                    <Card className='k-mt-3'>
                      <CardHeader>
                        <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                          <>
                            <Button
                              icon={'cancel'}
                              onClick={() => onExit()}
                              type='button'
                            >
                              Cancel
                            </Button>
                            <SplitButton
                              text='save'
                              themeColor={'primary'}
                              onButtonClick={() => onSave({ isExit: false })}
                              onItemClick={handleSaveSplit}
                            >
                              <SplitButtonItem text='Save & Close' />
                            </SplitButton>
                          </>
                        </div>
                      </CardHeader>
                    </Card>
                  )}
                </div>
              </>
            )}
          </React.Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};
export default AddBlob;
